/**
 * Exact decimal arithmetic on scaled BigInts, for the line editor's live totals. The backend uses
 * decimal.js with ROUND_HALF_UP (ACCOUNTING_RULES §1); every rounding step here is exact (rational) so the
 * preview matches what the server will store. No floating point is involved.
 */

/** Internal scale: values are integers in units of 10^-SCALE. Inputs carry at most 4–6 decimals. */
export const SCALE = 10;
const TEN = 10n;
const ONE = TEN ** BigInt(SCALE);

export type Dec = bigint;

export const ZERO: Dec = 0n;

const pow10 = (n: number) => TEN ** BigInt(n);

const DECIMAL_RE = /^(-)?(\d+)(?:\.(\d+))?$/;

/** Parses a plain decimal string ("12", "-3.5", "0.0001"); null when it is not a number. */
export function parse(value: string | number | null | undefined): Dec | null {
  if (value === null || value === undefined) return null;
  const s = typeof value === 'number' ? String(value) : value.trim();
  const m = DECIMAL_RE.exec(s);
  if (!m) return null;
  const [, sign, int = '0', frac = ''] = m;
  if (frac.length > SCALE) return null;
  const v = BigInt(int) * ONE + BigInt(frac.padEnd(SCALE, '0') || '0');
  return sign ? -v : v;
}

/** Like parse, but treats empty/invalid input as zero. */
export function dec(value: string | number | null | undefined): Dec {
  return parse(value) ?? ZERO;
}

export function fromInt(n: number): Dec {
  return BigInt(n) * ONE;
}

/** Number of decimals actually written in a string (for validation). */
export function decimalPlaces(value: string): number {
  const m = DECIMAL_RE.exec(value.trim());
  return m?.[3]?.length ?? 0;
}

/** a / b rounded half-up (away from zero) to an integer. */
function divHalfUp(a: bigint, b: bigint): bigint {
  if (b === 0n) throw new RangeError('Division by zero');
  const neg = a < 0n !== b < 0n;
  const x = a < 0n ? -a : a;
  const y = b < 0n ? -b : b;
  let q = x / y;
  const r = x % y;
  if (r * 2n >= y) q += 1n;
  return neg ? -q : q;
}

/** a / b truncated toward zero (exact rational division, ROUND_DOWN). */
function divDown(a: bigint, b: bigint): bigint {
  return a / b;
}

/** Rounds to `decimals` places with ROUND_HALF_UP. */
export function round(value: Dec, decimals: number): Dec {
  const unit = pow10(SCALE - decimals);
  return divHalfUp(value, unit) * unit;
}

/** a × b, rounded to `decimals` places (exact before the single rounding). */
export function mulRound(a: Dec, b: Dec, decimals: number): Dec {
  // a*b is at scale 2·SCALE; bring it to `decimals` in one rounding step.
  const unit = pow10(2 * SCALE - decimals);
  return divHalfUp(a * b, unit) * pow10(SCALE - decimals);
}

/** a × b without rounding (exact as long as the product fits SCALE decimals). */
export function mul(a: Dec, b: Dec): Dec {
  return divHalfUp(a * b, ONE);
}

/** a ÷ b rounded to `decimals` places. */
export function divRound(a: Dec, b: Dec, decimals: number): Dec {
  // (a/ONE) / (b/ONE) = a/b, expressed at `decimals`: a·10^d / b, then back to SCALE.
  return divHalfUp(a * pow10(decimals), b) * pow10(SCALE - decimals);
}

export function sum(values: Iterable<Dec>): Dec {
  let total = ZERO;
  for (const v of values) total += v;
  return total;
}

export function isInteger(value: Dec): boolean {
  return value % ONE === 0n;
}

/** Decimal places needed to write the value exactly. */
export function scaleOf(value: Dec): number {
  let v = value < 0n ? -value : value;
  if (v === 0n) return 0;
  let places = SCALE;
  while (places > 0 && v % TEN === 0n) {
    v /= TEN;
    places -= 1;
  }
  return places;
}

/** Fixed-point string with exactly `decimals` places (half-up), like decimal.js toFixed(d). */
export function toFixed(value: Dec, decimals: number): string {
  const r = round(value, decimals) / pow10(SCALE - decimals);
  const neg = r < 0n;
  const digits = (neg ? -r : r).toString().padStart(decimals + 1, '0');
  const int = decimals > 0 ? digits.slice(0, -decimals) : digits;
  const frac = decimals > 0 ? `.${digits.slice(-decimals)}` : '';
  return `${neg ? '-' : ''}${int}${frac}`;
}

/** Shortest exact string ("12", "2.5"). */
export function toPlain(value: Dec): string {
  return toFixed(value, scaleOf(value));
}

/** Converts to a JS number for display/charts only. */
export function toNumber(value: Dec): number {
  return Number(toPlain(value));
}

/**
 * Splits `total` across `weights` in proportion, rounded to `decimals`, so the parts sum exactly to
 * the total (largest-remainder method; ties go to the earliest index) — mirrors the backend's prorate.
 */
export function prorate(total: Dec, weights: Dec[], decimals: number): Dec[] {
  const amount = round(total, decimals);
  const weightSum = sum(weights);
  if (weights.some((w) => w < 0n)) throw new RangeError('Weights must not be negative');
  if (weightSum === 0n) {
    if (amount === 0n) return weights.map(() => ZERO);
    throw new RangeError('Cannot prorate a non-zero amount over zero weights');
  }
  const unitScaled = pow10(SCALE - decimals);
  // exact share_i = amount·w_i / weightSum; in units of 10^-decimals: amount·w_i / (weightSum·unitScaled)
  const denom = weightSum * unitScaled;
  const parts = weights.map((w, i) => {
    const numer = amount * w;
    const floored = divDown(numer, denom);
    return { i, floored, rem: numer - floored * denom };
  });
  let remainder = amount / unitScaled - sum(parts.map((p) => p.floored));
  const order = [...parts].sort((a, b) => (a.rem === b.rem ? a.i - b.i : a.rem > b.rem ? -1 : 1));
  const result = parts.map((p) => p.floored);
  for (const { i } of order) {
    if (remainder <= 0n) break;
    result[i] = (result[i] ?? 0n) + 1n;
    remainder -= 1n;
  }
  return result.map((r) => r * unitScaled);
}
